import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

// =================================================================================================
// ClickableCard — a keyboard-accessible drill-through wrapper.
//
// Wrap any KPI / panel content that should navigate on click. It renders a real focusable control
// (role="button" + tabIndex) so Enter/Space activate it and it gets a visible focus ring, then
// navigates with react-router on activation. A corner ↗ affordance signals "this drills in".
//
// USAGE — make a KpiCard navigate:
//
//   <ClickableCard to="/health" label="Open Fleet Health">
//     <KpiCard label="% critical" value={formatPct(pct)} source="rpt_hw_device_latest.risk_tier" />
//   </ClickableCard>
//
// It renders a native <button>, so Enter/Space activation and the focus ring come for free; the CSS
// resets the button's default chrome so the child keeps its own card styling. The wrapper only adds
// the hover lift, cursor, focus ring, and the ↗ glyph. Wrap only NON-interactive content (a KPI or
// a static chart panel) — never content that already contains a link/button. For TABLE ROWS use
// DataTable's `onRowClick` instead (it has the same affordances).
// =================================================================================================

export interface ClickableCardProps {
  // Internal react-router destination, e.g. "/health" or "/device/SERIAL123".
  readonly to: string;
  // Accessible name for the control (what activating it does), e.g. "Open Fleet Health".
  readonly label: string;
  readonly children: ReactNode;
}

export const ClickableCard = ({ to, label, children }: ClickableCardProps): ReactNode => {
  const navigate = useNavigate();
  const go = (): void => {
    void navigate(to);
  };
  return (
    <button type="button" className="clickable-card" aria-label={label} onClick={go}>
      <ArrowUpRight className="clickable-affordance" size={15} aria-hidden />
      {children}
    </button>
  );
};
